import { alert } from 'actions/flash'
import { api } from 'api/index'
import { useGetCustomerImportStatusQuery } from 'api/userLists'
import { buildTranslate } from 'locales'
import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectActiveImportFileUploadId,
  setActiveImportFileUploadId,
} from 'slices/userLists'

const t = buildTranslate('user_lists.import_history.importer')
const POLL_INTERVAL_MS = 5000

type ImportPollingState = 'idle' | 'polling' | 'finalized' | 'errored'

function getImportState(
  currentState: string | null,
  shouldPoll: boolean
): ImportPollingState {
  if (currentState === 'finalized') return 'finalized'
  if (currentState === 'errored') return 'errored'
  if (shouldPoll) return 'polling'
  return 'idle'
}

export default function useCustomerImportPolling(listId: number): {
  importState: ImportPollingState
  isPolling: boolean
} {
  const dispatch = useDispatch()
  const fileUploadId: number | null = useSelector(state =>
    selectActiveImportFileUploadId(state as any)
  )
  const prevStateRef = useRef<string | null>(null)

  const shouldPoll = fileUploadId !== null
  const { data } = useGetCustomerImportStatusQuery(
    { fileUploadId: fileUploadId! },
    {
      skip: !shouldPoll,
      pollingInterval: shouldPoll ? POLL_INTERVAL_MS : 0,
    }
  )

  const currentState = data?.state || null

  useEffect(() => {
    if (!currentState || currentState === prevStateRef.current) return
    prevStateRef.current = currentState

    if (currentState === 'finalized') {
      dispatch(setActiveImportFileUploadId(null))
      dispatch(
        api.util.invalidateTags([
          { type: 'UserList', id: `${listId}-imports` },
          { type: 'UserList', id: `${listId}-customers` },
        ])
      )
      dispatch(
        alert({
          key: 'success',
          message: t('success'),
          timeout: 5,
        })
      )
    } else if (currentState === 'errored') {
      dispatch(setActiveImportFileUploadId(null))
      dispatch(
        alert({
          key: 'danger',
          message: data?.error || t('error'),
          timeout: 5,
        })
      )
    }
  }, [currentState, dispatch, listId, data?.error])

  const importState = getImportState(currentState, shouldPoll)

  return {
    importState,
    isPolling: shouldPoll,
  }
}
